import { useCallback, useEffect, useRef } from "react";
import {
  getFleetStatus,
  getFleetStatusWithJobId,
  startFleetStatus,
} from "../lib/api";

const STATUS_CHECK_INTERVAL_MS = 30000;
const STATUS_CHECK_JOB_PAGE_SIZE = 100;

export const runFleetStatusCheck = async (unid) => {
  await startFleetStatus({ unid });

  const jobsResponse = await getFleetStatus({
    unid,
    pageNumber: 1,
    pageSize: STATUS_CHECK_JOB_PAGE_SIZE,
  });

  const jobs = jobsResponse?.data?.data?.jobs || [];
  const succeededJob = jobs.find((job) => job.status === "success");
  if (!succeededJob?.jobId) {
    return undefined;
  }

  const resultsResponse = await getFleetStatusWithJobId({
    unid,
    statusCheckId: succeededJob.jobId,
  });
  return resultsResponse?.data?.data?.job?.summary;
};

export const useFleetStatusCheck = ({ unid, isActive, onComplete }) => {
  const timeoutRef = useRef();
  const runIdRef = useRef(0);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const checkAndReschedule = useCallback(async (checkedUnid, runId) => {
    const isStillTheActiveRun = () => runId === runIdRef.current;
    try {
      const summary = await runFleetStatusCheck(checkedUnid);
      if (isStillTheActiveRun()) {
        await onCompleteRef.current?.(summary);
      }
    } catch (error) {
      console.error("Error during fleet status check:", error);
    }
    if (isStillTheActiveRun()) {
      timeoutRef.current = setTimeout(() => {
        checkAndReschedule(checkedUnid, runId);
      }, STATUS_CHECK_INTERVAL_MS);
    }
  }, []);

  useEffect(() => {
    if (!unid || !isActive) {
      return undefined;
    }

    runIdRef.current += 1;
    checkAndReschedule(unid, runIdRef.current);

    return () => {
      runIdRef.current += 1;
      clearTimeout(timeoutRef.current);
    };
  }, [unid, isActive, checkAndReschedule]);
};
